'use client';

import * as React from 'react';
import Link from 'next/link';

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuViewport,
} from '~/components/ui/navigation-menu';
import { type ProductCategory } from '~/utils/fecom-api-utils/category-helpers';
import { ProductCategoryNavTrigger } from './product-category-nav-trigger';
import { cn } from '~/lib/utils';

type ProductCategoryNavProps = {
  productCategories: ProductCategory[];
};

// Define the structure for a category
export interface Category {
  id: number;
  name: string;
  href: string;
  subcategories?: Category[];
}

// https://github.com/shadcn-ui/ui/issues/2671#issuecomment-2076503381

export function ProductCategoryNav({
  productCategories,
}: ProductCategoryNavProps) {
  const productCategoryItems: Category[] = productCategories.map(
    (category) => ({
      id: category.categoryId ?? 0,
      name: category.categoryName ?? '',
      href: category.fullUrlPath,
      subcategories: category.children?.map((child) => ({
        id: child.categoryId ?? 0,
        name: child.categoryName ?? '',
        href: child.fullUrlPath,
      })),
    }),
  );

  const linkStyle = cn(
    'product-category-nav__menu-link',
    'inline-block h-auto p-3 select-none rounded-md text-sm font-bold leading-tight no-underline outline-none transition-colors',
    'hover:bg-[var(--site-header-nav-item-hover)] hover:text-[var(--site-header-nav-item-text-hover)] focus:bg-[var(--site-header-nav-background)] focus:text-[var(--site-header-nav-foreground)] text-[var(--site-header-nav-foreground)] bg-[var(--site-header-nav-panel-background)] text-[var(--site-header-nav-panel-foreground)]',
  );

  return (
    <NavigationMenu
      orientation="horizontal"
      disableViewport
      delayDuration={300}
      className="product-category-nav w-full max-w-full justify-stretch [&>div]:max-w-full [&>div]:grow"
    >
      <NavigationMenuList className="w-full max-w-full items-stretch justify-between">
        {productCategoryItems.map((category) => (
          <NavigationMenuItem
            key={category.href}
            className="product-category-nav__menu-item"
          >
            <ProductCategoryNavTrigger category={category} />
            {
              // Render subcategories
              category.subcategories && category.subcategories.length > 0 && (
                <NavigationMenuContent>
                  <ul className="product-category-nav__menu-content w-full columns-4 px-4 py-4 xl:columns-5 2xl:columns-6">
                    {category.subcategories.map((subcat) => (
                      <NavigationMenuItem
                        className="product-category-nav__menu-item"
                        key={subcat.href}
                      >
                        <NavigationMenuLink className={linkStyle} asChild>
                          <Link key={subcat.id} href={subcat.href}>
                            {subcat.name}
                          </Link>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                    ))}
                  </ul>
                </NavigationMenuContent>
              )
            }
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
      <NavigationMenuViewport className="mt-0 rounded-t-none border-none bg-[var(--site-header-nav-panel-background)] text-[var(--site-header-nav-panel-foreground)] duration-200" />
    </NavigationMenu>
  );
}
